<template>
  <default-layout class="conversation-detail" unstick-header hide-mobile-back-button>
    <stale-item-alert
      v-if="showStaleAlert"
      :entity-type="EEntities.MAILBOXES"
      @refresh-requested="refreshStaleData"
    />
    <whos-viewing-indicator :item-id="conversationID" :larger-bottom-space="showStaleAlert" />
    <conversation-detail-topbar
      :ref-number="refNumber"
      :conversation-subject="formData.subject"
      :mailbox-id="mailboxID"
      :mailbox-name="mailboxName"
      :assignee-id="formData.assigneeID"
      :priority-id="formData.priorityID"
      :status-id="formData.statusID"
      :due-date="formData.dueDate"
      :status-options="statusOptions"
      :priority-options="priorityOptions"
      :assignees-options="assigneesOptions"
      :global-attachments="globalAttachments"
      :show-harvest-option="showHarvestOption"
      :disable-picker-options="addCommentOrReplyEditorOpened"
      :job-info="jobInfo"
      :is-pinned="isPinned"
      :spam-type="spamType"
      @update-status="(id) => updateField('status', id)"
      @update-priority="(id)=> updateField('priority', id)"
      @update-due-date="(date: string) => updateField('due-date', date)"
      @update-assignee="(id) => updateField('assignee', id)"
      @change-filter="(val) => goToListingWithFilter(val)"
      @add-custom-filter-clicked="$emit('add-custom-filter-clicked')"
      @navigate="(convo) => goToConversation(convo)"
      @move-clicked="showMoveConvoModal=true"
      @delete-clicked="handleDelete"
      @toggle-spam="toggleSpam"
      @back-button-clicked="handleBackButtonClick"
      @show-harvest-modal="showHarvestModal=true"
      @pin-toggled="togglePin()"
    />
    <main-container size="big" no-top-padding>
      <entity-child-layout>
        <item-detail-navigator
          :current-ref-number="refNumber"
          @navigate="(conversation) => goToConversation(conversation)"
          @back-button-clicked="handleBackButtonClick"
        />
        <conversation-detail-header
          v-bind="formData"
          :is-data-loading="isDataLoading"
          :mailbox-name="mailboxName"
          :ref-number="refNumber"
          :creation-date="creationDate"
          :show-harvest-option="showHarvestOption"
          :status-options="statusOptions"
          :priority-options="priorityOptions"
          :assignees-options="assigneesOptions"
          :tags-options="tagsOptions"
          :is-toggling-spam="isTogglingSpam"
          :other-conversations="otherConversations"
          :checklists="checklists"
          :linked-tasks="linkedTasks"
          :creation-event="creationEvent"
          :global-attachments="globalAttachments"
          :is-pinned="isPinned"
          :job-info="jobInfo"
          :spam-type="spamType"
          class="conversation-detail__header"
          @update-status="(id) => updateField('status', id)"
          @update-priority="(id)=> updateField('priority', id)"
          @update-due-date="(date: string) => updateField('due-date', date)"
          @update-assignee="(id) => updateField('assignee', id)"
          @update-tags="(tags) => updateTags(tags)"
          @update-subject="(subject) => updateField('subject', subject)"
          @update-job-appointee="(jobID, appointeeID) => updateJobAppointee(jobID, appointeeID)"
          @update-custom-field="fetchData()"
          @customer-name-updated="(name) => updateCustomerNameField(name)"
          @customer-updated="(email) =>updateCustomerField(email)"
          @update-incoming-message="(desc) => updateField('incoming-message', desc)"
          @move-clicked="showMoveConvoModal=true"
          @delete-clicked="handleDelete"
          @toggle-spam="toggleSpam"
          @linked-tasks-updated="refreshLinkedTasks"
          @show-harvest-modal="showHarvestModal=true"
          @pin-toggled="togglePin()"
        />
        <conversation-detail-chronology
          v-if="!isDataLoading"
          :due-date="formData.dueDate"
          :assignee-id="formData.assigneeID"
          :priority-id="formData.priorityID"
          :status-id="formData.statusID"
          :cc="formData.cc"
          :customer="formData.customer"
          :list-watchers="formData.listWatchers"
          :is-submitting="isSubmitting"
          :is-traffic-cop-checking="isTrafficCopChecking"
          :is-post-submit-pre-refetch-data="isPostSubmitPreRefetchData"
          :responders-since-last-view="respondersSinceLastView"
          :mailbox-id="mailboxID"
          :conversation-id="conversationID"
          :profile="profile"
          :items="historyWithTypes"
          :status-options="statusOptions"
          :priority-options="priorityOptions"
          :assignees-options="assigneesOptions"
          :watchers-options="watchersOptions"
          :mention-users="atMentionUsers"
          :job-info="jobInfo"
          :comment-submit-handler="editComment"
          :incoming-reply-submit-handler="editIncomingReply"
          :saved-replies="savedReplies"
          :reply-signature="replySignature"
          @editor-opened="(val) => handleEditorToggle(val)"
          @update-watchers="(ids) => updateField('watchers', ids)"
          @update-cc="updateCC"
          @submit="submitResponse"
          @confirmed-traffic-cop-note="refreshHistory()"
          @original-message-requested="(replyInfo) => setStylizedHtmlLinkToDisplay(replyInfo)"
        />
      </entity-child-layout>
    </main-container>
    <!-- HARVEST MODAL -->
    <harvest-modal
      v-if="showHarvestOption"
      v-model:show-modal="showHarvestModal"
      :perma-link="permaLink"
      :entity-name="mailboxName"
      :item-title="formData.subject"
      :item-id="conversationID"
      :ref-number="refNumber"
    />
    <!-- HTML EVENT VERSION VIEWER -->
    <conversation-detail-html-event-version-viewer
      v-if="hasStylizedHtmlLinkToDisplay"
      :stylized-html-description-link="stylizedHtmlLinkToDisplay"
      @canceled="stylizedHtmlLinkToDisplay=null"
    />
    <!-- MOVE ITEMS MODAL -->
    <bulk-move-items-modal
      v-if="showMoveConvoModal"
      :entity-id="mailboxID"
      :selected-item-ids="[ conversationID ]"
      is-convo-related
      @edit-canceled="showMoveConvoModal=false"
      @edit-completed="(newMailboxID) => handleConvoMoveCompleted(newMailboxID)"
    />
  </default-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { isEqual, uniq } from 'lodash-es'
import config from '@/config'
import DefaultLayout from '@/layouts/default-layout.vue'
import MainContainer from '@/layouts/main-container.vue'
import EntityChildLayout from '@/layouts/entity-child-layout.vue'

/* Mixins */

/* Components */
import ConversationDetailTopbar from './components/conversation-detail-topbar.vue'
import ConversationDetailHeader from './components/conversation-detail-header.vue'
import ConversationDetailChronology from './components/conversation-detail-chronology.vue'
import ConversationDetailHtmlEventVersionViewer from './components/conversation-detail-html-event-version-viewer.vue'

/* Helpers */
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import LocalStore from '@/helpers/localstore'
import TypeAssistant from '@/helpers/type-assistant'

/* Data types */
import {
  IChronologyItemTypes,
  IChronologyItem,
  IChronologyReply,
} from '@/types/data-objects/item-histories'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IAtMentionUser,
  IPriorityLevelForSelection,
  IStatusForSelection,
  IConvertedSavedReply,
  IConvosOutputHistoryv12,
  ILinkedItemForListing,
  IPersonWithEmail,
  IItemResponder,
  IJobAppointee,
  IJobAssigneeMandate,
  IChecklist,
} from '@/api/types/response.types'
import {
  IConvosInputCommentWithUpdate,
  IConvosInputEditComment,
  IConvosInputReplyToCustomerWithUpdate,
  IConvosInputUpdateJobAppointee,
} from '@/api/types/request.types'
import { IProfile } from '@/types/data-objects/profile'
import {
  IBreadcrumbItemMenuItem,
  ICommentOrReplyData,
  IJobInfo,
} from '@/types/data-objects/items'
import { EEntities, SpamTypes } from '@/types'

/* API */
import ConversationsAPI from '@/api/modules/convos.api'
import CustomersAPI from '@/api/modules/customers.api'
import DashboardAPI from '@/api/modules/dashboard.api'
import { useAppStore } from '@/modules/app/app.store'

export default defineComponent({
  name: 'ConversationDetail',

  components: {
    DefaultLayout,
    MainContainer,
    EntityChildLayout,
    ConversationDetailTopbar,
    ConversationDetailHeader,
    ConversationDetailChronology,
    ConversationDetailHtmlEventVersionViewer,
  },

  emits: ['add-custom-filter-clicked'],

  data() {
    return {
      /* Read once, not computed from $route: the page never changes ID while shown (the router-view is keyed by path),
         but its late awaits and timers run after a navigation, when $route has the next page's params (NaN or another ID) */
      accountID: parseInt(this.$route.params.accountId, 10),
      mailboxID: Number(this.$route.params.id),
      conversationID: Number(this.$route.params.childId),
      EEntities,

      // Details
      refNumber: 0,
      mailboxName: '',
      creationDate: '',
      showHarvestOption: false,
      tags: [] as string[],
      globalAttachments: [] as any[],
      tagsOptions: [] as string[],
      atMentionUsers: [] as IAtMentionUser[],
      respondersSinceLastView: [] as IItemResponder[],
      isPinned: false,
      jobAppointees: [] as IJobAppointee[],

      // For job info...
      currentAssignee: null as any,
      currentStatus: null as any,

      // Convo-specific
      customerAddressDeactivated: false,
      replySignature: '',
      spamType: SpamTypes.NOT_SPAM,

      // Options
      statusOptions: [] as IStatusForSelection[],
      priorityOptions: [] as IPriorityLevelForSelection[],
      assigneesOptions: [] as IPersonWithEmail[],
      watchersOptions: [] as IPersonWithEmail[],

      // UI controls
      isDataLoading: true,
      isSubmitting: false,
      isPostSubmitPreRefetchData: false,
      sendBackButtonToRoot: false,
      addCommentOrReplyEditorOpened: false,
      showHarvestModal: false,
      isTrafficCopChecking: false,
      isTogglingSpam: false,
      showMoveConvoModal: false,

      formData: {
        dueDate: null as any,
        assigneeID: 0,
        priorityID: 0,
        statusID: 0,
        subject: '',
        customer: {} as IPersonWithEmail,
        cc: [] as any[],
        listTags: [] as any[],
        listWatchers: [] as any[],
      },
      history: [] as IConvosOutputHistoryv12[],
      checklists: [] as IChecklist[],
      linkedTasks: [] as ILinkedItemForListing[],
      otherConversations: [] as ILinkedItemForListing[],
      savedReplies: [] as IConvertedSavedReply[],
      assigneeMandates: [] as IJobAssigneeMandate[],
      stylizedHtmlLinkToDisplay: null as any,

      creationEvent: {} as IConvosOutputHistoryv12,

      /* Polling data */
      showStaleAlert: false,
      lastAccessEpochTime: null as any,
      poller: null as any,
      pollIntervalInMS: useAppStore().autoRefreshDataPingInMS,
      isUnmounted: false,
    }
  },

  head(): any {
    if (this.refNumber !== null && this.formData.subject) {
      return {
        title: `#${this.refNumber}: ${this.formData.subject}`,
      }
    } else if (RouteAssistant.routeState(this.$route, 'title')) {
      return { title: `${RouteAssistant.routeState(this.$route, 'title')}` }
    } else {
      return {
        title: config.title,
        titleTemplate: `%s `,
      }
    }
  },

  computed: {
    profile(): IProfile {
      return this.$permissions.loggedInUser
    },
    permaLink(): string {
      return window.location.href
    },
    isSpam(): boolean {
      return TypeAssistant.isSpam(this.spamType)
    },
    hasStylizedHtmlLinkToDisplay(): boolean {
      return this.stylizedHtmlLinkToDisplay !== null
    },
    historyWithTypes(): IChronologyItem[] {
      const result = this.history
        .slice(1)
        .map((item: IConvosOutputHistoryv12) => {
          if (item.isConvoThread) {
            if (item.isOutsideUserInitiated) {
              return {
                data: { ...item },
                type: IChronologyItemTypes.REPLY_FROM_OUTSIDE_USER,
              }
            }
            return {
              data: { ...item },
              type: IChronologyItemTypes.REPLY_TO_CUSTOMER,
            }
          } else if (item.description || item.attachments.length > 0) {
            return {
              data: {
                ...item,
                mentionUsers: this.atMentionUsers,
              },
              type: IChronologyItemTypes.COMMENT,
            }
          } else {
            return {
              data: { ...item },
              type: IChronologyItemTypes.ACTION,
            }
          }
        })

      return result
    },
    jobInfo(): IJobInfo {
      if (this.jobAppointees.length === 0) {
        return null
      } else {
        return {
          jobAppointees: this.jobAppointees,
          assigneeMandates: this.assigneeMandates,
          currentAssignee: this.currentAssignee,
          currentStatus: this.currentStatus,
        }
      }
    },
  },

  beforeUnmount() {
    this.isUnmounted = true
    this.endPolling()
  },

  async created() {
    this.isDataLoading = true
    try {
      await this.fetchData()
    } catch (error) {
      this.$error.emit(error)
    } finally {
      this.isDataLoading = false
    }
    this.startPolling()
  },

  mounted() {
    // Pre-seed data from the route.params, if available (coming from internal routing)
    // This will help breadcrumb-for-item-detail feel "smooth" page-to-page since doesn't have to wait
    // for data load. Data load is still good in case project name changed in interim, for example.
    if (RouteAssistant.routeState(this.$route, 'refNumber')) {
      this.refNumber = parseInt(RouteAssistant.routeState(this.$route, 'refNumber'), 10)
    }
    // Specifically useful for "from_all" or going from linked tasks. The "from_entity_landing" scenario
    // will have data passed from VueX cache.
    if (RouteAssistant.routeState(this.$route, 'mailboxName')) {
      this.mailboxName = RouteAssistant.routeState(this.$route, 'mailboxName')
    }

    // NOTE: LocalStore.itemDetailBackBtnToRoot only set to true when navigating the prev/next buttons
    // or coming in here from being freshly created. So, the very first landing of a detail from the
    // listing pages will not have this be true and it will simply lean on window.history.go(-1). Subsequent
    // ones will be true and then go back to the listing where item filter settings are fetched
    // from localstore (and btw, we hard set the page back to 1 always. Maybe this should be left so
    // they stay on the page they had come from...but should move pagination to the top of listing for clarity)
    if (LocalStore.itemDetailBackBtnToRoot) {
      this.sendBackButtonToRoot = true
      LocalStore.itemDetailBackBtnToRoot = false
    }

    /* Show deactivated customer modal if necessary */
    setTimeout(() => {
      if (
        !this.customerAddressDeactivated ||
        this.formData.customer.email.includes('reply@')
      ) {
        return
      }
      this.showCustomerDeactivatedModal()
    }, 1000)
  },

  methods: {
    clearTrafficCop() {
      this.respondersSinceLastView = [] as IItemResponder[]
    },

    async refreshHistory() {
      // Only refetch history. Refetching convo details will
      // override any to or cc updates made by the user since those
      // vars have been tightly-coupled to the formdata.
      await this.fetchHistory()
      await this.$nextTick()

      this.clearTrafficCop()
      this.scrollToLatestUpdates()

      // Restart polling that was halted with traffic cop check...
      this.startPolling()
    },
    async refreshStaleData() {
      await this.fetchData()
      this.scrollToLatestUpdates()
    },
    scrollToLatestUpdates() {
      const newestHistory = this.history.find(h => !h.hasViewed)

      // Perhaps user clicked on an exposed "stale-item-alert" which then
      // loaded the new updates, etc.
      if (newestHistory === undefined) {
        return
      }

      const firstUnviewedEventID = this.history
        .find(h => !h.hasViewed)
        .id.toString()

      const element = document.getElementById(`e${firstUnviewedEventID}`)
      const navBarOffset = this.$screen.isMobile(this.$mq) ? 64 : 36

      window.scrollTo({
        top:
          document.documentElement.scrollTop +
          element.getBoundingClientRect().top -
          navBarOffset,
        behavior: 'smooth',
      })
    },
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      // A load or save that finishes after the user left would otherwise start a poller nothing clears
      if (this.isUnmounted) {
        return
      }
      this.poller = setInterval(async () => {
        try {
          await this.checkForStaleData()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async checkForStaleData() {
      const lastUpdatedEpochTime = await ConversationsAPI.get_LastUpdatedEpochTime(
        this.mailboxID,
        this.conversationID,
      )
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        this.showStaleAlert = true
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    handleEditorToggle(opened: boolean) {
      this.addCommentOrReplyEditorOpened = opened
    },
    handleConvoMoveCompleted(newMailboxID: number) {
      this.showMoveConvoModal = false
      RouteAssistant.goToConversation(
        this.$router,
        this.$route.params.accountId,
        newMailboxID,
        this.conversationID,
        this.mailboxName,
        this.refNumber,
        this.formData.subject,
      )
    },
    async fetchData() {
      this.showStaleAlert = false
      // Fetch new data
      await Promise.all([
        this.fetchConversationData(),
        this.fetchOptions(),
        this.fetchHistory(),
      ])
    },

    async fetchOptions() {
      const response = await ConversationsAPI.get_FilterOptionsWithConvoID(
        this.mailboxID,
        this.conversationID,
      )

      this.statusOptions = response.listStatuses
      this.priorityOptions = response.listPriorities
      this.watchersOptions = response.listWatchers
      this.assigneesOptions = response.listAssignees
      this.tagsOptions = response.listTags
      this.atMentionUsers = response.listAtMentionUsers
      this.savedReplies = response.listSavedReplies
    },

    async fetchHistory() {
      const response = await ConversationsAPI.get_History_v1_2(
        this.mailboxID,
        this.conversationID,
        'chron',
      )

      const allAttachments: IAttachment[] = []
      response.forEach(item => {
        allAttachments.push(...item.attachments)
      })
      this.globalAttachments = uniq(allAttachments)
      this.history = response

      /* First message data pulled off the first history item */
      this.creationEvent = this.history[0]
    },

    async fetchConversationData() {
      const response = await ConversationsAPI.get_Detail(
        this.mailboxID,
        this.conversationID,
      )

      this.lastAccessEpochTime = response.accessEpochTime

      this.refNumber = response.refNumber
      this.creationDate = response.createdOn
      this.mailboxName = response.mailbox.name
      this.tags = response.listTags
      this.showHarvestOption = response.harvestEnabled
      this.isPinned = response.isPinned
      this.jobAppointees = response.listJobAppointees
      this.assigneeMandates = response.listAssigneeMandates
      this.currentAssignee = response.assignee
      this.currentStatus = response.status
      this.customerAddressDeactivated = response.customerAddressDeactivated
      this.spamType = TypeAssistant.getSpamType(response.spamStatus)

      this.formData.subject = response.subject
      this.formData.assigneeID = response.assignee ? response.assignee.id : null
      this.formData.statusID = response.status.id
      this.formData.priorityID = response.priority.id
      this.formData.customer = response.customer
      this.formData.cc = response.cc
      this.formData.listTags = response.listTags
      this.formData.dueDate = response.dueDate
      this.formData.listWatchers = response.listWatchers.map(item => item.id)

      this.otherConversations = response.listOtherConversations
      this.linkedTasks = response.listLinkedTasks
      this.checklists = response.listChecklists
      this.replySignature = response.replySignature
    },

    async refreshLinkedTasks() {
      try {
        this.linkedTasks = await ConversationsAPI.get_LinkedTasks(
          this.mailboxID,
          this.conversationID,
        )
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    setStylizedHtmlLinkToDisplay(replyInfo: IChronologyReply) {
      this.stylizedHtmlLinkToDisplay = replyInfo.stylizedHtmlVersionLink
    },

    showCustomerDeactivatedModal() {
      this.$confirm(
        `<strong>` +
          this.formData.customer.email +
          `</strong><br />Messages sent to this address have previously failed.
          We recommend you delete this conversation or update the customer's address before replying.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'OK',
          type: 'warning',
          title: 'Suspicious Email',
        },
      )
    },

    handleBackButtonClick() {
      if (this.sendBackButtonToRoot) {
        try {
          this.$router.push(LocalStore.lastItemListRouteVisited)
          return
        } catch {
          // Do nothing, just fallback to base case...
        }
      }

      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.goToMailbox()
      }
    },

    async getRespondersSinceLastViewed(): Promise<IItemResponder[]> {
      this.clearTrafficCop()
      this.isTrafficCopChecking = true
      const responders = await ConversationsAPI.get_RespondersSinceLastViewed(
        this.mailboxID,
        this.conversationID,
      )
      this.isTrafficCopChecking = false
      return responders
    },

    submitResponse(data: ICommentOrReplyData) {
      const isReply: boolean = data.hasOwnProperty('cc')
      if (isReply) {
        this.createReply({
          to: data.to,
          cc: data.cc,
          message: data.message,
          assigneeID: data.assigneeID,
          statusID: data.statusID,
          priorityID: data.priorityID,
          dueDate: data.dueDate,
          listAtMentionIDs: data.listAtMentionIDs,
          listFileUploadIDs: data.listFileUploadIDs,
        })
      } else {
        this.createComment({
          message: data.message,
          assigneeID: data.assigneeID,
          statusID: data.statusID,
          priorityID: data.priorityID,
          dueDate: data.dueDate,
          listAtMentionIDs: data.listAtMentionIDs,
          listFileUploadIDs: data.listFileUploadIDs,
        })
      }
    },

    async createReply(formData: IConvosInputReplyToCustomerWithUpdate) {
      // Temporarily halt polling...
      this.showStaleAlert = false
      this.endPolling()

      // Get traffic cop info & return if exists...
      const responders = await this.getRespondersSinceLastViewed()
      if (responders.length > 0) {
        this.respondersSinceLastView = responders
        // Note: Polling will continue to halt until user explicitly refreshes for responder updates.
        return
      }

      try {
        this.isSubmitting = true
        const response = await ConversationsAPI.post_ReplyToCustomerWithUpdate(
          this.mailboxID,
          this.conversationID,
          formData,
        )

        this.isPostSubmitPreRefetchData = true
        this.showSuccess(response.message)
        await this.fetchData()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isPostSubmitPreRefetchData = false
        this.isSubmitting = false
        this.startPolling()
      }
    },

    async createComment(formData: IConvosInputCommentWithUpdate) {
      // Temporarily halt polling...
      this.showStaleAlert = false
      this.endPolling()

      // Get traffic cop info & return if exists...
      const responders = await this.getRespondersSinceLastViewed()
      if (responders.length > 0) {
        this.respondersSinceLastView = responders
        // Note: Polling will continue to halt until user explicitly refreshes for responder updates.
        return
      }

      try {
        this.isSubmitting = true
        const response = await ConversationsAPI.post_CommentWithUpdate(
          this.mailboxID,
          this.conversationID,
          formData,
        )

        this.isPostSubmitPreRefetchData = true
        this.showSuccess(response.message)
        await this.fetchData()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isPostSubmitPreRefetchData = false
        this.isSubmitting = false
        this.startPolling()
      }
    },

    async editComment(commentId: number, formData: IConvosInputEditComment) {
      try {
        const response = await ConversationsAPI.put_EditComment(
          this.mailboxID,
          this.conversationID,
          commentId,
          formData,
        )
        this.fetchData()
        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async editIncomingReply(replyId: number, message: string) {
      try {
        const response = await ConversationsAPI.put_EditIncomingMessage(
          this.mailboxID,
          this.conversationID,
          replyId,
          message,
        )
        this.fetchData()
        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    updateCustomerNameField(name: string) {
      if (name) {
        this.formData.customer.name = name
        this.updateField('customer-name', name)
      }
    },

    updateCustomerField(email: string) {
      if (email) {
        this.formData.customer.email = email
        this.formData.customer.name = email
        this.formData.customer.id = 0
        this.updateField('customer', email)
      }
    },

    updateCC(value: string[]) {
      this.formData.cc = value
    },

    updateTags(newVal: string[]) {
      if (!isEqual(this.tags, newVal)) {
        this.updateField('tags', newVal)
        this.tags = newVal
      }
    },

    async updateField(field: string, value: any) {
      if (this.isDataLoading) {
        return
      }
      try {
        let response
        let requiresRefresh = true

        switch (field) {
          case 'subject':
            response = await ConversationsAPI.put_Subject(
              this.mailboxID,
              this.conversationID,
              value,
            )
            break
          case 'status':
            response = await ConversationsAPI.put_Status(
              this.mailboxID,
              this.conversationID,
              value,
            )
            break
          case 'priority':
            response = await ConversationsAPI.put_PriorityLevel(
              this.mailboxID,
              this.conversationID,
              value,
            )
            break
          case 'assignee':
            response = await ConversationsAPI.put_Assignee(
              this.mailboxID,
              this.conversationID,
              value,
            )
            break
          case 'due-date':
            response = await ConversationsAPI.put_DueDate(
              this.mailboxID,
              this.conversationID,
              value,
            )
            break
          case 'watchers':
            response = await ConversationsAPI.put_Watchers(
              this.mailboxID,
              this.conversationID,
              value,
            )
            requiresRefresh = false
            break
          case 'tags':
            response = await ConversationsAPI.put_Tags(
              this.mailboxID,
              this.conversationID,
              value,
            )
            break
          case 'customer-name':
            response = await CustomersAPI.put_UpdateName(
              this.formData.customer.id,
              value,
            )
            break
          case 'customer':
            response = await ConversationsAPI.put_Customer(
              this.mailboxID,
              this.conversationID,
              value,
            )
            break
          case 'incoming-message':
            response = await ConversationsAPI.put_EditIncomingMessage(
              this.mailboxID,
              this.conversationID,
              this.creationEvent.id,
              value,
            )
            break
        }
        if (requiresRefresh) {
          this.fetchData()
        }

        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async updateJobAppointee(jobID: number, appointeeID: number) {
      if (this.isDataLoading) {
        return
      }
      try {
        const data = {
          appointeeID,
          jobID,
        } as IConvosInputUpdateJobAppointee
        const response = await ConversationsAPI.put_JobAppointee(
          this.mailboxID,
          this.conversationID,
          data,
        )
        this.fetchData()
        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async deleteConversation() {
      try {
        const response = await ConversationsAPI.delete_Delete(
          this.mailboxID,
          this.conversationID,
        )
        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        // Don't pull the user back if they already went somewhere else
        if (!this.isUnmounted) {
          this.goToMailbox()
        }
      }
    },

    async togglePin() {
      try {
        let response
        if (this.isPinned) {
          response = await DashboardAPI.delete_DeletePinnedItem(
            this.conversationID,
          )
        } else {
          response = await DashboardAPI.post_AddPinnedItem(this.conversationID)
        }
        this.showSuccess(response.message)
        this.isPinned = !this.isPinned
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async toggleSpam() {
      try {
        this.isTogglingSpam = true
        if (this.isSpam) {
          const response = await ConversationsAPI.put_RemoveFromSpam(
            this.mailboxID,
            this.conversationID,
          )
        } else {
          const response = await ConversationsAPI.put_MarkAsSpam(
            this.mailboxID,
            this.conversationID,
          )
        }
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.fetchData()
        this.isTogglingSpam = false
      }
    },

    goToMailbox() {
      RouteAssistant.goToMailbox(
        this.$router,
        this.$route.params.accountId,
        this.$route.params.id,
        this.mailboxName,
      )
    },

    showSuccess(message: string) {
      this.$messaging.showSuccess(message)
    },

    goToConversation(convo: IBreadcrumbItemMenuItem) {
      /* 
        Continue sending item detail back button to root if
        going between prev/next items 
      */
      LocalStore.itemDetailBackBtnToRoot = true

      /* Handles situation where data may not be loaded yet */
      if (convo.entityID === 0 || convo.itemID === 0) {
        return
      }

      RouteAssistant.goToConversation(
        this.$router,
        this.$route.params.accountId,
        convo.entityID,
        convo.itemID,
        convo.entityName,
        convo.refNumber,
        convo.title,
        false,
        null,
        RouteAssistant.routeState(this.$route, 'from_all'),
        RouteAssistant.routeState(this.$route, 'from_entity_home'),
      )
    },

    goToListingWithFilter(val: string) {
      if (RouteAssistant.routeState(this.$route, 'from_entity_home')) {
        this.goToMailboxHomeWithFilter(val)
      } else if (RouteAssistant.routeState(this.$route, 'from_all')) {
        this.goToAllConversationsWithFilter(val)
      }
    },

    goToMailboxHomeWithFilter(val: string) {
      if (ItemFilterAssistant.isDefaultIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToMailboxWithDefaultFilter(
          this.$router,
          this.$route.params.accountId,
          this.$route.params.id,
          filterID,
        )
      } else if (ItemFilterAssistant.isCustomIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToMailboxWithCustomFilter(
          this.$router,
          this.$route.params.accountId,
          this.$route.params.id,
          filterID,
        )
      }
    },

    goToAllConversationsWithFilter(val: string) {
      if (ItemFilterAssistant.isDefaultIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToAllConversationsWithDefaultFilter(
          this.$router,
          this.$route.params.accountId,
          filterID,
        )
      } else if (ItemFilterAssistant.isCustomIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToAllConversationsWithCustomFilter(
          this.$router,
          this.$route.params.accountId,
          filterID,
        )
      }
    },

    handleDelete() {
      this.$confirm(
        `<strong>This action can’t be undone.</strong>
        <br>Are you sure you want to delete this conversation?`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: 'Delete conversation',
        },
      ).then(() => {
        this.deleteConversation()
      })
    },
  },
})
</script>
<style lang="scss">
.conversation-detail {
  &__header {
    // Offsets the item-detail-navigator-tablet-desktop height since
    // item-detail-navigator-tablet-desktop is sticky positioned, so has to
    // take up the space sbove task-detail-header before the stickiness takes affect.
    @include media-breakpoint-up(md) {
      margin-top: -80px;
    }
  }
}
</style>
